<template>
  <div :class="[$style.alert, classes]" :style="style">
    <div :class="$style.icon" v-if="icon">
      <component :is="icon" />
    </div>

    <div :class="$style.body">
      <div :class="$style.title" v-if="source.title">
        <span :class="$style.text">{{ source.title }}</span>
      </div>
      <span :class="$style.message">
        <span :class="$style.text">{{ source.message }}</span>
      </span>
    </div>

    <button :class="$style.close" @click="$emit('close', source.id)">
      <icon-close />
    </button>

    <div :class="$style.timeout" v-if="source.options.duration"></div>
  </div>
</template>

<script lang="ts" setup>
import IconClose from './icons/close.vue'
import IconWarning from './icons/warning.vue'
import IconBell from './icons/bell.vue'
import IconCheck from './icons/check.vue'
import type { Snack } from '../types'

defineOptions({
  name: 'SnacksItem'
})

const props = defineProps<{
  source: Snack
}>()

defineEmits<{
  close: [sourceId: Snack['id']]
}>()

const style = computed(() => ({
  ...(Number.isFinite(props.source.options.duration)
    ? { '--dur': `${props.source.options.duration}ms` }
    : {})
}))

const cssModule = useCssModule()

const classes = computed(() => cssModule[props.source.type])

const icon = computed(() => {
  switch (props.source.type) {
    case 'info':
      return IconBell

    case 'success':
      return IconCheck

    case 'warning':
    case 'errorHigh':
    case 'error':
      return IconWarning

    default:
      return null
  }
})
</script>

<style lang="scss" module>
.alert {
  transition:
    transform 0.5s cubic-bezier(0.785, 0.135, 0.15, 1),
    opacity 0.5s cubic-bezier(0.785, 0.135, 0.15, 1);
  pointer-events: all;
  color: color-get(--root-rgb-text-1);
  margin-bottom: 1.5em;
  border-radius: 0.8em;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  overflow-wrap: break-word;
  position: relative;
  flex-wrap: nowrap;
  box-shadow: 0 0 51.6px 0 rgba(0, 0, 0, 0.25);
  opacity: 1;
  text-align: left;
  padding: 1.4em;
  backdrop-filter: blur(6px);
  background-color: color-get(--root-rgb-bg-dark);
  overflow: hidden;
  width: 100%;
}
.icon {
  position: relative;
  padding-top: 0.3em;
  color: #fff;
  align-self: flex-start;
  margin-right: 1.6em;
  svg {
    width: 2.4em;
    height: auto;
  }
}
.message {
  .text {
    font-weight: 400;
    font-size: 1.6em;
    line-height: 138%;
  }
}
.title {
  .text {
    font-weight: 500;
    font-size: 1.8em;
    line-height: 144%;
  }
}
.close {
  color: color-get(--root-rgb-text-2);
  position: absolute;
  top: 1.4em;
  right: 1.4em;
  cursor: pointer;
  background: none;
  border-radius: 0;
  outline: none;
  svg {
    width: 2em;
    height: 2em;
  }
  &:hover {
    opacity: 0.5;
  }
}

.error {
  color: color-get(--root-rgb-system-error);

  .icon {
    color: color-get(--root-rgb-system-error);
  }

  .timeout {
    &::after {
      background: color-get(--root-rgb-system-error);
    }
  }
}
.errorHigh {
  color: color-get(--root-rgb-system-error);

  .icon {
    color: color-get(--root-rgb-system-error);
  }

  .timeout {
    &::after {
      background: color-get(--root-rgb-system-error);
    }
  }
}
.increase,
.success {
  color: color-get(--root-rgb-system-success);

  .icon {
    color: color-get(--root-rgb-system-success);
  }

  .timeout {
    &::after {
      background: color-get(--root-rgb-system-success);
    }
  }
}

.warning {
  color: color-get(--root-rgb-system-warning);

  .icon {
    color: color-get(--root-rgb-system-warning);
  }

  .timeout {
    &::after {
      background: color-get(--root-rgb-system-warning);
    }
  }
}
.decrease,
.info {
  color: color-get(--root-rgb-text-1);

  .icon {
    color: color-get(--root-rgb-text-1);
  }

  .timeout {
    &::after {
      background: color-get(--root-rgb-text-1);
    }
  }
}
.decrease,
.increase {
  max-width: 26em;
  .text {
    font-family: var(--primary-font), sans-serif;
    font-style: var(--primary-font-style);
    font-weight: 700;
    font-size: 2em;
  }
}
.timeout {
  overflow: hidden;
  display: flex;
  align-items: flex-end;
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 2em;
  border-radius: inherit;
  &:after {
    display: block;
    width: 100%;
    background-color: color-get(--root-rgb-text-1);
    height: 2px;
    content: '';
    animation: timeout var(--dur) forwards linear;
    transform-origin: 0 0;
  }
}
@keyframes timeout {
  0% {
    transform: scale(1, 1);
  }
  100% {
    transform: scale(0, 1);
  }
}
</style>
